import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';

import DataTable from '../../../components/DataTable';
import { useDetailTireInventoryList, useDetailTireFitList, useDetailTireRepairScrapList } from '../../../stores/tireInventoryStore';
import { useLanguage } from '../../../utils/i18n';

export default function DetailTireInventory({ open, onClose, batchRow, selectedCompanies }) {
  const { t } = useLanguage();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const batchNumber = batchRow?.batch_number || '';

  const [searchField, setSearchField] = useState('serial_number');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const searchFieldOptions = useMemo(() => [{ value: 'serial_number', label: t('tyreItem.serialNumber', 'Serial Number') }], [t]);

  const [activeTab, setActiveTab] = useState(0);

  const {
    data: invData,
    totalRecord: invTotal,
    isLoading: invLoading
  } = useDetailTireInventoryList(paginationModel.page, paginationModel.pageSize, selectedCompanies, batchNumber, searchField, searchValue);

  const {
    data: fitData,
    totalRecord: fitTotal,
    isLoading: fitLoading
  } = useDetailTireFitList(paginationModel.page, paginationModel.pageSize, selectedCompanies, batchNumber, searchField, searchValue);

  const {
    data: rsData,
    totalRecord: rsTotal,
    isLoading: rsLoading
  } = useDetailTireRepairScrapList(
    paginationModel.page,
    paginationModel.pageSize,
    selectedCompanies,
    batchNumber,
    searchField,
    searchValue
  );

  const currentData = activeTab === 0 ? invData : activeTab === 1 ? fitData : rsData;
  const currentTotal = activeTab === 0 ? invTotal : activeTab === 1 ? fitTotal : rsTotal;
  const currentLoading = activeTab === 0 ? invLoading : activeTab === 1 ? fitLoading : rsLoading;

  const totalQtyAll = (invTotal || 0) + (fitTotal || 0) + (rsTotal || 0);

  const handleTabChange = (e, val) => {
    setActiveTab(val);
    setPaginationModel({ page: 0, pageSize: 100 });
    setSearchRaw('');
    setSearchValue('');
  };

  const getValidValueWithSource = (v1, v2, v3) => {
    if (v1 != null && v1 !== 0 && String(v1) !== '0')
      return { value: v1, source: t('detailTireInventory.lastInspection', 'Last Inspection') };
    if (v2 != null && v2 !== 0 && String(v2) !== '0') return { value: v2, source: t('detailTireInventory.base', 'Base') };
    if (v3 != null && v3 !== 0 && String(v3) !== '0') return { value: v3, source: t('detailTireInventory.standard', 'Standard') };
    return { value: null, source: null };
  };

  const columns = useMemo(() => {
    const cols = [
      {
        field: 'serial_number',
        headerName: t('tyreItem.serialNumber', 'Serial Number'),
        flex: 1,
        valueGetter: (value, row) => row.serial_number || '-'
      },
      {
        field: 'air_pressure',
        headerName: t('dashboardAsset.airPressure', 'Air Pressure'),
        flex: 1,
        renderCell: (params) => {
          const row = params.row;
          const result = getValidValueWithSource(
            row?.last_inspection?.air_pressure,
            row?.air_pressure,
            row?.join?.m_tire_spec_pressure_std
          );
          if (!result.value) return '-';
          return (
            <Box display="flex" flexDirection="column">
              <Typography variant="body2">{result.value}</Typography>
              <Typography variant="caption" color="text.secondary">
                {result.source}
              </Typography>
            </Box>
          );
        }
      },
      {
        field: 'tread_depth',
        headerName: t('dashboardAsset.treadDepth', 'Tread Depth'),
        flex: 1,
        renderCell: (params) => {
          const row = params.row;
          const result = getValidValueWithSource(
            row?.last_inspection?.tread_depth,
            row?.tread_depth,
            row?.join?.m_tire_spec_original_tread_depth
          );
          if (!result.value) return '-';
          return (
            <Box display="flex" flexDirection="column">
              <Typography variant="body2">{result.value}</Typography>
              <Typography variant="caption" color="text.secondary">
                {result.source}
              </Typography>
            </Box>
          );
        }
      }
    ];

    if (activeTab === 1) {
      // Fit
      cols.push({
        field: 'last_vehicle',
        headerName: t('general.unit', 'Unit'),
        flex: 1,
        valueGetter: (value, row) => row.last_vehicle || '-'
      });
      cols.push({
        field: 'last_position',
        headerName: t('general.position', 'Position'),
        flex: 1,
        valueGetter: (value, row) => row.last_position || '-'
      });
    } else {
      // Inventory or Repair
      cols.push({
        field: 'last_vehicle',
        headerName: t('detailTireInventory.lastUnit', 'Last Unit'),
        flex: 1,
        valueGetter: (value, row) => row.last_vehicle || '-'
      });
      cols.push({
        field: 'last_position',
        headerName: t('detailTireInventory.lastPosition', 'Last Position'),
        flex: 1,
        valueGetter: (value, row) => row.last_position || '-'
      });
    }

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, t]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {t('detailTireInventory.title', 'Detail Tire Inventory')} - {t('tireReceiving.batchNo', 'Batch')}: {batchNumber}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Bagian Kiri: Informasi Data Batch */}
          <Grid item size={{ xs: 12, md: 4 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('detailTireInventory.batchInformation', 'Batch Information')}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('general.company', 'Company')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {batchRow?.company_name || '-'}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('tireReceiving.batchNo', 'Batch Number')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {batchNumber || '-'}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('detailTireInventory.initialQty', 'Initial Qty')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {batchRow?.qty ?? '-'}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('detailTireInventory.totalCurrentQty', 'Total Current Qty')}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {totalQtyAll}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('detailTireInventory.inventoryQty', 'Inventory Qty')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {invTotal || 0}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('detailTireInventory.installedQty', 'Installed Qty')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {fitTotal || 0}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('detailTireInventory.repairScrapQty', 'Repair/Scrap Qty')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {rsTotal || 0}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('specification.brand', 'Brand')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {batchRow?.tire_brand_name || '-'}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('specification.size', 'Size')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {batchRow?.tire_size_code || '-'}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('detailTireInventory.specPattern', 'Spec / Pattern')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {batchRow?.pattern || batchRow?.tire_spec_name || '-'}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('warehouse.name', 'Warehouse')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {batchRow?.warehouse_name || '-'}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('tireReceiving.suplier', 'Supplier')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {batchRow?.supplier || '-'}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('tireReceiving.receivedDate', 'Receive Date')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {batchRow?.receive_date && batchRow.receive_date !== '-' ? dayjs(batchRow.receive_date).format('DD-MM-YYYY') : '-'}
                    </Typography>
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('tireReceiving.unitPrice', 'Unit Price')}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {batchRow?.unit_price != null
                        ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(Number(batchRow.unit_price))
                        : '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Bagian Kanan: List Serial Number */}
          <Grid item size={{ xs: 12, md: 8 }}>
            <Box sx={{ minHeight: 400, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                  <Tab label={`${t('detailTireInventory.inventory', 'Inventory')} (${invTotal || 0})`} />
                  <Tab label={`${t('detailTireInventory.installed', 'Installed')} (${fitTotal || 0})`} />
                  <Tab label={`${t('detailTireInventory.repairScrap', 'Repair/Scrap')} (${rsTotal || 0})`} />
                </Tabs>
              </Box>
              <DataTable
                rows={currentData || []}
                columns={columns}
                loading={currentLoading}
                pageSizeOptions={[10, 25, 50, 100]}
                paginationMode="server"
                rowCount={currentTotal || 0}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                hideAddButton={true}
                hideExportButton={true}
                searchQuery={searchRaw}
                onSearch={({ field, value }) => {
                  setSearchField(field || 'all');
                  setSearchRaw(value);
                  setSearchValue(value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                searchFieldOptions={searchFieldOptions}
                selectedSearchField={searchField}
                onSearchFieldChange={(f) => {
                  const safeField = typeof f === 'object' ? f?.value : f;
                  setSearchField(safeField || 'all');
                  setSearchRaw('');
                  setSearchValue('');
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                hideSearch={false}
                hideCompanyFilter={true}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
